import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export interface BookingLineInput {
  /** Satt for linjer som finnes fra før. */
  id?: string;
  item_id: string;
  quantity: number;
  date_from: string;
  date_to: string;
  note: string | null;
}

export interface BookingInput {
  project_id: string | null;
  title: string | null;
  responsible_user_id: string;
  items: BookingLineInput[];
}

export interface BookingWarnings {
  booking_id: string;
  conflicts: {
    booking_item_id: string;
    item_id: string;
    item_name: string;
    other_label: string | null;
    other_quantity: number;
    other_date_from: string;
    other_date_to: string;
    other_responsible_name: string | null;
  }[];
}

/**
 * Bookinglinjene og konfliktene leses fra views over begge tabellene, og en
 * konflikt rører også det andre prosjektet, så linjenøklene invalideres bredt.
 * Audit-triggerne skriver historikken på utstyret og prosjektloggen.
 */
async function invalidateBooking(qc: QueryClient, bookingId: string, projectIds: (string | null)[]) {
  await Promise.all([
    invalidateFor(qc, "equipment_booking_items"),
    invalidateFor(qc, "equipment_bookings", { bookingId }),
    invalidateFor(qc, "event_log"),
    ...projectIds.filter((p): p is string => !!p).map((projectId) => invalidateFor(qc, "project_events", { projectId })),
  ]);
}

/**
 * Lagre en booking via RPC create_equipment_booking / update_equipment_booking.
 * Returnerer konflikter og avdelingsadvarsler. RLS avgjør hvem som kan endre;
 * aktøren i audit er den ekte innloggede brukeren (auth.uid() i triggerne).
 */
export function useSaveEquipmentBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bookingId,
      input,
    }: {
      bookingId: string | null;
      input: BookingInput;
      previousProjectId?: string | null;
    }): Promise<BookingWarnings> => {
      const { data, error } = bookingId
        ? await supabase.rpc("update_equipment_booking", { p_booking_id: bookingId, payload: input })
        : await supabase.rpc("create_equipment_booking", { payload: input });
      if (error) throw new Error(`Kunne ikke lagre bookingen: ${error.message}`);
      return data as BookingWarnings;
    },
    onSuccess: async (warnings, { input, previousProjectId }) => {
      await invalidateBooking(qc, warnings.booking_id, [input.project_id, previousProjectId ?? null]);
    },
  });
}

/** Avbestill en booking. Bookinger slettes ikke. */
export function useCancelEquipmentBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string; projectId: string | null }) => {
      const { data, error } = await supabase
        .from("equipment_bookings")
        .update({ status: "cancelled" })
        .eq("id", bookingId)
        .select("id");
      if (error) throw new Error(`Kunne ikke avbestille: ${error.message}`);
      if ((data ?? []).length !== 1) throw new Error("Du har ikke tilgang til å avbestille denne bookingen");
    },
    onSuccess: async (_data, { bookingId, projectId }) => {
      await invalidateBooking(qc, bookingId, [projectId]);
    },
  });
}
